import { Platform } from 'react-native';
import { REVENUECAT_CONFIG, DEVELOPMENT_CONFIG } from '@/utils/revenueCatConfig';
import { createTracer } from '@/utils/requestTracer';
import { timeoutLogger, TimeoutErrorCode, TimeoutSeverity } from '@/utils/timeoutLogger';
import NetInfo from '@react-native-community/netinfo';

// Global top-level singleton guard to prevent ANY duplicate configure() calls
// Note: __RCConfigured is set in app/_layout.tsx at app startup
declare global {
  var __RCConfigured: boolean | undefined;
  var __RCConfiguring: Promise<void> | undefined;
}

type CustomerInfo = any;
type CustomerInfoListener = (customerInfo: CustomerInfo) => void;

interface RevenueCatService {
  isInitialized: boolean;
  isInitializing: boolean;
  isBillingClientReady: boolean;
  billingClientRetryCount: number;
  purchases: any;
  customerInfoListeners: Set<CustomerInfoListener>;
  lastCustomerInfo: CustomerInfo | null;
  lastCustomerInfoFetch: number;
  initializationPromise: Promise<void> | null;
  pendingRequests: Map<string, Promise<any>>;
  credentialErrorCount: number;
  lastCredentialError: number | null;
  currentAppUserId: string | null;
  userIdentificationComplete: boolean;
  lastOperationTimestamps: Map<string, number>;
}

class RevenueCatServiceManager {
  private static instance: RevenueCatServiceManager;
  private service: RevenueCatService = {
    isInitialized: false,
    isInitializing: false,
    isBillingClientReady: false,
    billingClientRetryCount: 0,
    purchases: null,
    customerInfoListeners: new Set(),
    lastCustomerInfo: null,
    lastCustomerInfoFetch: 0,
    initializationPromise: null,
    pendingRequests: new Map(),
    credentialErrorCount: 0,
    lastCredentialError: null,
    currentAppUserId: null,
    userIdentificationComplete: false,
    lastOperationTimestamps: new Map(),
  };
  private readonly MIN_OPERATION_DELAY = 300;
  private readonly ANDROID_BILLING_CLIENT_DELAY = 500;
  private readonly ANDROID_BILLING_CLIENT_MAX_RETRIES = 5;
  private readonly ANDROID_BILLING_CLIENT_RETRY_BASE_DELAY = 300;
  private readonly HEALTH_REPORT_TIMEOUT = 5000;
  private readonly CUSTOMER_INFO_TIMEOUT_POOR_NETWORK = 15000;
  private readonly CUSTOMER_INFO_TIMEOUT_GOOD_NETWORK = 10000;
  private networkQuality: string = 'UNKNOWN';

  private constructor() {
    this.initializeNetworkMonitoring();
  }

  private async initializeNetworkMonitoring(): Promise<void> {
    try {
      const state = await NetInfo.fetch();
      this.updateNetworkQuality(state);
      NetInfo.addEventListener(state => {
        this.updateNetworkQuality(state);
      });
    } catch (error) {
      console.warn('🛒 [RevenueCat Service] Failed to initialize network monitoring:', error);
    }
  }

  private updateNetworkQuality(state: any): void {
    if (!state.isConnected) {
      this.networkQuality = 'OFFLINE';
    } else if (state.type === 'wifi') {
      this.networkQuality = 'EXCELLENT';
    } else if (state.type === 'cellular') {
      const generation = (state.details as any)?.cellularGeneration || 'unknown';
      if (generation === '5g') this.networkQuality = 'EXCELLENT';
      else if (generation === '4g') this.networkQuality = 'GOOD';
      else if (generation === '3g') this.networkQuality = 'POOR';
      else this.networkQuality = 'POOR';
    } else {
      this.networkQuality = 'GOOD';
    }
  }

  private getTimeoutForOperation(operation: string): number {
    if (operation === 'health_report') {
      return this.HEALTH_REPORT_TIMEOUT;
    }

    if (operation === 'getCustomerInfo' || operation === 'logIn') {
      return this.networkQuality === 'POOR' || this.networkQuality === 'OFFLINE'
        ? this.CUSTOMER_INFO_TIMEOUT_POOR_NETWORK
        : this.CUSTOMER_INFO_TIMEOUT_GOOD_NETWORK;
    }

    return this.networkQuality === 'POOR' ? 20000 : 15000;
  }

  static getInstance(): RevenueCatServiceManager {
    if (!RevenueCatServiceManager.instance) {
      RevenueCatServiceManager.instance = new RevenueCatServiceManager();
    }
    return RevenueCatServiceManager.instance;
  }

  async initialize(appUserId: string): Promise<void> {
    if (Platform.OS === 'web') {
      console.log('🛒 [RevenueCat Service] Skipping initialization on web platform');
      return;
    }

    // CRITICAL: Require authenticated user ID - no anonymous initialization allowed
    if (!appUserId) {
      const error = new Error('RevenueCat initialization requires authenticated user ID');
      console.error('🛒 [RevenueCat Service] ❌', error.message);
      console.error('🛒 [RevenueCat Service] Anonymous initialization is not allowed to prevent transfer issues');
      throw error;
    }

    if (this.service.isInitialized) {
      console.log('🛒 [RevenueCat Service] Already initialized, skipping');
      return;
    }

    if (this.service.isInitializing) {
      console.log('🛒 [RevenueCat Service] Initialization in progress, waiting...');
      return this.service.initializationPromise!;
    }

    this.service.isInitializing = true;
    this.service.initializationPromise = (async () => {
      try {
        console.log('🛒 [RevenueCat Service] Initializing with authenticated user:', appUserId);
        const purchasesModule = await import('react-native-purchases');
        this.service.purchases = purchasesModule.default;

        if (DEVELOPMENT_CONFIG.debugLogging) {
          const logLevel = purchasesModule.LOG_LEVEL?.DEBUG || 'DEBUG';
          this.service.purchases.setLogLevel(logLevel);
          console.log('🛒 [RevenueCat Service] Log level set to DEBUG');
        }

        const apiKey = Platform.OS === 'ios'
          ? REVENUECAT_CONFIG.ios.apiKey
          : REVENUECAT_CONFIG.android.apiKey;

        if (!apiKey) {
          throw new Error(`No API key configured for ${Platform.OS}`);
        }

        const configOptions: any = {
          apiKey,
          appUserID: appUserId,
          ...(Platform.OS === 'android' && {
            showInAppMessagesAutomatically: false,
            // Additional Android-specific configuration to prevent automatic billing operations
            observerMode: false,
            useAmazon: false
          })
        };

        console.log('🛒 [RevenueCat Service] Configuration options:', {
          platform: Platform.OS,
          hasAppUserId: !!appUserId,
          androidInAppMessages: Platform.OS === 'android' ? 'disabled' : 'N/A',
          networkQuality: this.networkQuality
        });

        // Set up global error handler to suppress non-critical billing wrapper errors
        if (Platform.OS === 'android') {
          this.setupAndroidErrorHandler();
        }

        const tracer = createTracer('RevenueCat SDK Configuration', 10000);
        tracer.addCheckpoint('Starting configure()');

        try {
          await this.service.purchases.configure(configOptions);
          tracer.addCheckpoint('configure() completed');
        } catch (error) {
          const duration = tracer.getDuration();
          tracer.timeout(duration);
          await timeoutLogger.logTimeout({
            requestId: tracer.getRequestId(),
            operationName: 'RevenueCat SDK Configuration',
            timeoutDuration: 10000,
            actualDuration: duration,
            errorCode: TimeoutErrorCode.REVENUECAT_INIT,
            severity: TimeoutSeverity.HIGH,
            metadata: {
              platform: Platform.OS,
              appUserId,
              networkQuality: this.networkQuality,
              error: error instanceof Error ? error.message : String(error)
            }
          });
          throw error;
        }
        globalThis.__RCConfigured = true;
        this.service.currentAppUserId = appUserId;
        this.service.userIdentificationComplete = true;

        // ANDROID BILLING CLIENT FIX:
        // On Android, the BillingWrapper needs time to attach listeners after configure()
        // Without this delay, operations like showInAppMessages will fail with
        // "BillingWrapper is not attached to a listener" error
        // Increased delay to 1000ms and enhanced retry mechanism for better reliability
        if (Platform.OS === 'android') {
          console.log('🛒 [RevenueCat Service] [Android] Waiting for billing client to attach...');
          console.log('🛒 [RevenueCat Service] [Android] Initial delay: ' + this.ANDROID_BILLING_CLIENT_DELAY + 'ms');
          await new Promise(resolve => setTimeout(resolve, this.ANDROID_BILLING_CLIENT_DELAY));

          // Verify billing client is ready by attempting a simple operation
          tracer.addCheckpoint('Starting billing client verification');
        await this.verifyBillingClientReady();
        tracer.addCheckpoint('Billing client verified');
        }

        this.service.purchases.addCustomerInfoUpdateListener((customerInfo: CustomerInfo) => {
          console.log('🛒 [RevenueCat Service] Customer info updated (global listener)');
          this.service.lastCustomerInfo = customerInfo;
          this.service.lastCustomerInfoFetch = Date.now();

          this.service.customerInfoListeners.forEach(listener => {
            try {
              listener(customerInfo);
            } catch (error) {
              console.error('🛒 [RevenueCat Service] Error in customer info listener:', error);
            }
          });
        });

        this.service.isInitialized = true;
        tracer.complete({ success: true });
        console.log('🛒 [RevenueCat Service] ✓ Initialization complete for user:', appUserId);

        this.scheduleBackgroundHealthReportCheck();
      } catch (error) {
        console.error('🛒 [RevenueCat Service] ❌ Initialization error:', error);
        this.service.purchases = null;
        globalThis.__RCConfigured = false;
        throw error;
      } finally {
        this.service.isInitializing = false;
      }
    })();

    return this.service.initializationPromise;
  }

  private setupAndroidErrorHandler(): void {
    // Set up a global error handler to suppress non-critical billing wrapper errors
    // that occur from automatic background operations
    console.log('🛒 [RevenueCat Service] [Android] Setting up error handler for billing wrapper issues');

    // Note: We can't directly intercept RevenueCat's internal errors, but we can
    // ensure our verification is thorough and our operations are properly guarded
  }

  private scheduleBackgroundHealthReportCheck(): void {
    if (this.networkQuality === 'POOR' || this.networkQuality === 'OFFLINE') {
      console.log('🛒 [RevenueCat Service] 📡 Skipping health report check on poor network during initialization');
      console.log('🛒 [RevenueCat Service] 📡 Health report will be attempted in background after app is initialized');
      setTimeout(() => this.performBackgroundHealthReportCheck(), 10000);
      return;
    }

    console.log('🛒 [RevenueCat Service] 📡 Network quality good, health report check will run normally');
  }

  private async performBackgroundHealthReportCheck(): Promise<void> {
    if (!this.service.isInitialized || !this.service.purchases) {
      console.log('🛒 [RevenueCat Service] ⚠️ Health report check skipped: SDK not initialized');
      return;
    }

    console.log('🛒 [RevenueCat Service] 📡 Starting background health report check...');
    const tracer = createTracer('RevenueCat Background Health Report', this.HEALTH_REPORT_TIMEOUT);

    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Health report timeout')), this.HEALTH_REPORT_TIMEOUT)
      );

      const customerInfoPromise = this.service.purchases.getCustomerInfo();

      await Promise.race([customerInfoPromise, timeoutPromise]);
      tracer.complete({ success: true, source: 'background' });
      console.log('🛒 [RevenueCat Service] ✓ Background health report check completed');
    } catch (error: any) {
      const duration = tracer.getDuration();
      const isTimeout = error?.message?.includes('timeout') || duration >= this.HEALTH_REPORT_TIMEOUT;

      if (isTimeout) {
        tracer.timeout(duration);
        console.log('🛒 [RevenueCat Service] ⚠️ Background health report timed out (non-blocking)');
        console.log('🛒 [RevenueCat Service] This is expected on poor networks and does not affect functionality');

        await timeoutLogger.logTimeout({
          requestId: tracer.getRequestId(),
          operationName: 'RevenueCat Background Health Report',
          timeoutDuration: this.HEALTH_REPORT_TIMEOUT,
          actualDuration: duration,
          errorCode: TimeoutErrorCode.REVENUECAT_HEALTH_REPORT,
          severity: TimeoutSeverity.NON_CRITICAL,
          metadata: {
            platform: Platform.OS,
            networkQuality: this.networkQuality,
            source: 'background',
            note: 'Non-blocking diagnostic operation',
          },
        });
      } else {
        console.warn('🛒 [RevenueCat Service] Background health report error (non-blocking):', error?.message);
      }
    }
  }

  private async verifyBillingClientReady(): Promise<void> {
    if (Platform.OS !== 'android') {
      this.service.isBillingClientReady = true;
      return;
    }

    let retries = 0;
    while (retries < this.ANDROID_BILLING_CLIENT_MAX_RETRIES) {
      try {
        console.log(`🛒 [RevenueCat Service] [Android] Verifying billing client (attempt ${retries + 1}/${this.ANDROID_BILLING_CLIENT_MAX_RETRIES})...`);

        // Attempt multiple lightweight operations to thoroughly verify billing client is ready
        // Use short timeout for verification to fail fast if billing client not ready
        const verificationTimeout = 5000;
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Billing client verification timeout')), verificationTimeout)
        );

        // getCustomerInfo is safe and doesn't trigger UI, unlike showInAppMessages
        await Promise.race([
          this.service.purchases.getCustomerInfo(),
          timeoutPromise
        ]);

        // Additional verification: Try to get offerings to ensure full billing client readiness
        // Note: offerings may be empty if products aren't configured in the store yet - that's OK
        try {
          await Promise.race([
            this.service.purchases.getOfferings(),
            timeoutPromise
          ]);
        } catch (offeringsError: any) {
          const msg = offeringsError?.message || '';
          if (msg.includes('products') || msg.includes('offerings') || msg.includes('empty')) {
            console.warn('🛒 [RevenueCat Service] [Android] Offerings empty/unavailable during verification (non-blocking - products may not be configured in store yet)');
          } else {
            throw offeringsError;
          }
        }

        this.service.isBillingClientReady = true;
        this.service.billingClientRetryCount = retries;
        console.log('🛒 [RevenueCat Service] [Android] ✓ Billing client fully verified and ready');
        console.log('🛒 [RevenueCat Service] [Android] Verification completed after ' + retries + ' retries');
        return;
      } catch (error: any) {
        const errorMsg = error?.message || '';

        if (errorMsg.includes('BillingWrapper') || errorMsg.includes('not attached')) {
          console.warn(`🛒 [RevenueCat Service] [Android] ⚠️ Billing client not ready yet, retrying...`);
          retries++;

          if (retries < this.ANDROID_BILLING_CLIENT_MAX_RETRIES) {
            // Exponential backoff: 500ms, 1000ms, 1500ms, 2000ms, 2500ms
            const delay = this.ANDROID_BILLING_CLIENT_RETRY_BASE_DELAY * retries;
            console.log(`🛒 [RevenueCat Service] [Android] Waiting ${delay}ms before retry...`);
            await new Promise(resolve => setTimeout(resolve, delay));
          }
        } else {
          // Different error - could be network, credentials, etc.
          // Log but don't fail initialization
          console.warn('🛒 [RevenueCat Service] [Android] Non-billing client error during verification:', error);
          console.warn('🛒 [RevenueCat Service] [Android] Error type:', error?.code || 'unknown');
          this.service.isBillingClientReady = true;
          return;
        }
      }
    }

    // Even if verification failed, mark as ready to avoid blocking the app
    // The actual operations will handle billing client errors appropriately
    console.warn('🛒 [RevenueCat Service] [Android] ⚠️ Billing client verification timeout after ' + retries + ' attempts');
    console.warn('🛒 [RevenueCat Service] [Android] Total wait time: ' + (this.ANDROID_BILLING_CLIENT_DELAY + (this.ANDROID_BILLING_CLIENT_RETRY_BASE_DELAY * retries * (retries + 1) / 2)) + 'ms');
    console.warn('🛒 [RevenueCat Service] [Android] Marking as ready anyway - operations will handle errors individually');
    this.service.isBillingClientReady = true;
    this.service.billingClientRetryCount = retries;
  }

  private async waitForBillingClient(): Promise<void> {
    if (Platform.OS !== 'android') {
      return;
    }

    if (this.service.isBillingClientReady) {
      return;
    }

    console.log('🛒 [RevenueCat Service] [Android] Waiting for billing client to be ready...');
    let attempts = 0;
    const maxAttempts = 100; // 10 seconds max wait (increased from 5 seconds)

    while (!this.service.isBillingClientReady && attempts < maxAttempts) {
      await new Promise(resolve => setTimeout(resolve, 100));
      attempts++;
    }

    if (!this.service.isBillingClientReady) {
      console.warn('🛒 [RevenueCat Service] [Android] ⚠️ Billing client still not ready after waiting ' + (attempts * 100) + 'ms');
      console.warn('🛒 [RevenueCat Service] [Android] This may indicate a device-specific issue or slow initialization');
    } else {
      console.log('🛒 [RevenueCat Service] [Android] ✓ Billing client ready after waiting ' + (attempts * 100) + 'ms');
    }
  }

  getPurchases(): any {
    return this.service.purchases;
  }

  isReady(): boolean {
    const baseReady = this.service.isInitialized && this.service.purchases !== null;

    // On Android, also check billing client readiness
    if (Platform.OS === 'android') {
      return baseReady && this.service.isBillingClientReady;
    }

    return baseReady;
  }

  // Forwards the GA4 app instance id to RevenueCat ($firebaseAppInstanceId
  // reserved attribute) so the RevenueCat -> Google Analytics integration can
  // attribute server-side subscription events (renewals, cancellations,
  // expirations) to the same Firebase Analytics user.
  async setFirebaseAppInstanceId(appInstanceId: string): Promise<void> {
    if (!this.service.purchases) {
      return;
    }

    try {
      await this.service.purchases.setAttributes({ $firebaseAppInstanceId: appInstanceId });
      console.log('🛒 [RevenueCat Service] $firebaseAppInstanceId attribute set');
    } catch (error) {
      console.warn('🛒 [RevenueCat Service] Failed to set $firebaseAppInstanceId:', error);
    }
  }

  getBillingClientStatus(): { ready: boolean; retryCount: number; diagnostics: string } {
    const totalInitTime = this.ANDROID_BILLING_CLIENT_DELAY +
      (this.ANDROID_BILLING_CLIENT_RETRY_BASE_DELAY * this.service.billingClientRetryCount * (this.service.billingClientRetryCount + 1) / 2);

    let diagnostics = '';
    if (Platform.OS === 'android') {
      diagnostics = `Android billing client status:\n` +
        `- Ready: ${this.service.isBillingClientReady}\n` +
        `- Retry count: ${this.service.billingClientRetryCount}\n` +
        `- Total initialization time: ~${totalInitTime}ms\n` +
        `- SDK initialized: ${this.service.isInitialized}\n` +
        `- Current user: ${this.service.currentAppUserId || 'none'}`;
    } else {
      diagnostics = 'Billing client status is Android-only';
    }

    return {
      ready: this.service.isBillingClientReady,
      retryCount: this.service.billingClientRetryCount,
      diagnostics
    };
  }

  addCustomerInfoListener(listener: CustomerInfoListener, expectedUserId?: string): () => void {
    this.service.customerInfoListeners.add(listener);

    if (this.service.lastCustomerInfo) {
      const cachedUserId = this.service.lastCustomerInfo.originalAppUserId;
      const isAnonymousData = cachedUserId?.startsWith('$RCAnonymousID:');
      const isUserMismatch = expectedUserId && cachedUserId && cachedUserId !== expectedUserId;

      if (isAnonymousData || isUserMismatch) {
        console.log('🛒 [RevenueCat Service] Skipping cached customer info for new listener:');
        console.log('🛒 [RevenueCat Service]   - Cached ID:', cachedUserId);
        console.log('🛒 [RevenueCat Service]   - Expected ID:', expectedUserId || 'not specified');
        console.log('🛒 [RevenueCat Service]   - Is anonymous:', isAnonymousData);
        console.log('🛒 [RevenueCat Service]   - Is mismatch:', isUserMismatch);
        console.log('🛒 [RevenueCat Service] Listener will receive fresh data when available');
      } else {
        try {
          listener(this.service.lastCustomerInfo);
        } catch (error) {
          console.error('🛒 [RevenueCat Service] Error calling new listener with cached info:', error);
        }
      }
    }

    return () => {
      this.service.customerInfoListeners.delete(listener);
    };
  }

  private async waitForMinDelay(operationKey: string): Promise<void> {
    const lastTimestamp = this.service.lastOperationTimestamps.get(operationKey) || 0;
    const now = Date.now();
    const timeSinceLastOp = now - lastTimestamp;

    if (timeSinceLastOp < this.MIN_OPERATION_DELAY) {
      const waitTime = this.MIN_OPERATION_DELAY - timeSinceLastOp;
      console.log(`🛒 [RevenueCat Service] Throttling ${operationKey}, waiting ${waitTime}ms`);
      await new Promise(resolve => setTimeout(resolve, waitTime));
    }

    this.service.lastOperationTimestamps.set(operationKey, Date.now());
  }

  private async retryOnConcurrentError<T>(operation: () => Promise<T>, operationName: string, maxRetries = 3): Promise<T> {
    let lastError: Error | undefined;

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error: any) {
        lastError = error;
        const errorMsg = error?.message || '';

        if (errorMsg.includes('already in progress')) {
          const delay = Math.min(100 * Math.pow(2, attempt), 1000);
          console.warn(`🛒 [RevenueCat Service] ${operationName} - concurrent operation detected, retry ${attempt + 1}/${maxRetries} in ${delay}ms`);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }

        throw error;
      }
    }

    console.error(`🛒 [RevenueCat Service] ${operationName} - max retries exceeded`);
    throw lastError || new Error(`${operationName} failed after ${maxRetries} retries`);
  }

  async getCustomerInfo(forceRefresh = false): Promise<CustomerInfo> {
    if (!this.isReady()) {
      throw new Error('RevenueCat not initialized');
    }

    // Wait for billing client on Android
    await this.waitForBillingClient();

    const cacheMaxAge = 30000;
    const now = Date.now();
    const cacheAge = now - this.service.lastCustomerInfoFetch;

    if (!forceRefresh && this.service.lastCustomerInfo && cacheAge < cacheMaxAge) {
      console.log(`🛒 [RevenueCat Service] Returning cached customer info (age: ${cacheAge}ms)`);
      return this.service.lastCustomerInfo;
    }

    const requestKey = 'getCustomerInfo';
    const existingRequest = this.service.pendingRequests.get(requestKey);

    if (existingRequest) {
      console.log('🛒 [RevenueCat Service] Deduplicating getCustomerInfo request');
      return existingRequest;
    }

    const request = (async () => {
      const timeout = this.getTimeoutForOperation('getCustomerInfo');
      const tracer = createTracer('RevenueCat getCustomerInfo', timeout);

      try {
        await this.waitForMinDelay(requestKey);
        tracer.addCheckpoint('Rate limit delay completed');

        console.log('🛒 [RevenueCat Service] Fetching customer info from API...');
        console.log('🛒 [RevenueCat Service] Network quality:', this.networkQuality, '| Timeout:', timeout, 'ms');

        tracer.addCheckpoint('Starting API request');

        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Customer info request timeout')), timeout)
        );

        const customerInfo = await Promise.race([
          this.retryOnConcurrentError(
            () => this.service.purchases.getCustomerInfo(),
            'getCustomerInfo'
          ),
          timeoutPromise
        ]);

        tracer.addCheckpoint('API request completed');
        this.service.lastCustomerInfo = customerInfo;
        this.service.lastCustomerInfoFetch = Date.now();
        tracer.complete({ success: true, cached: false });
        return customerInfo;
      } catch (error: any) {
        const duration = tracer.getDuration();
        const errorCode = error?.code || error?.userInfo?.code;
        const errorMsg = error?.message || '';
        const isTimeout = errorMsg.includes('timeout') || duration >= timeout;

        if (isTimeout) {
          tracer.timeout(duration);
          await timeoutLogger.logTimeout({
            requestId: tracer.getRequestId(),
            operationName: 'RevenueCat getCustomerInfo',
            timeoutDuration: timeout,
            actualDuration: duration,
            errorCode: TimeoutErrorCode.REVENUECAT_CUSTOMER_INFO,
            severity: TimeoutSeverity.HIGH,
            metadata: {
              platform: Platform.OS,
              networkQuality: this.networkQuality,
              forceRefresh,
              hasCachedInfo: !!this.service.lastCustomerInfo,
            },
          });

          // Return cached info if available on timeout
          if (this.service.lastCustomerInfo) {
            console.log('🛒 [RevenueCat Service] ⚠️ Timeout - returning cached customer info as fallback');
            return this.service.lastCustomerInfo;
          }
        }

        if (errorCode === 'InvalidCredentialsError' || errorMsg.includes('Invalid Play Store credentials')) {
          this.service.credentialErrorCount++;
          this.service.lastCredentialError = Date.now();

          console.error(
            '\n' +
            '='.repeat(80) + '\n' +
            '🛒 REVENUECAT CREDENTIAL ERROR\n' +
            '='.repeat(80) + '\n' +
            'Google Play Store credentials are invalid or not properly configured.\n' +
            '\n' +
            'This error occurs when:\n' +
            '1. Service Account credentials are not uploaded to RevenueCat dashboard\n' +
            '2. Credentials are still propagating (takes 24-48 hours after upload)\n' +
            '3. App is not published to at least Internal/Closed Testing track\n' +
            '4. Package name mismatch between app and RevenueCat configuration\n' +
            '\n' +
            'REQUIRED SETUP STEPS:\n' +
            '1. Create Google Play Service Account with "Finance" permissions\n' +
            '2. Upload Service Account JSON to RevenueCat dashboard\n' +
            '3. Publish app to Internal or Closed Testing track in Play Console\n' +
            '4. Wait 24-48 hours OR use quick activation workaround:\n' +
            '   - Edit any subscription description in Play Console and save\n' +
            '5. Verify package name matches: com.virgoplay.hekaya\n' +
            '\n' +
            'Error count: ' + this.service.credentialErrorCount + '\n' +
            'For detailed setup instructions, visit:\n' +
            'https://www.revenuecat.com/docs/creating-play-service-credentials\n' +
            '='.repeat(80) + '\n'
          );

          if (this.service.lastCustomerInfo) {
            console.warn('🛒 [RevenueCat Service] Returning cached customer info due to credential error');
            return this.service.lastCustomerInfo;
          }

          return {
            entitlements: { active: {} },
            activeSubscriptions: [],
            allPurchasedProductIdentifiers: [],
            _offline: true
          };
        }

        if (errorMsg.includes('already in progress') || errorMsg.includes('429')) {
          console.warn('🛒 [RevenueCat Service] Concurrent operation or rate limit, returning cached info if available');
          if (this.service.lastCustomerInfo) {
            return this.service.lastCustomerInfo;
          }
        }
        throw error;
      } finally {
        this.service.pendingRequests.delete(requestKey);
      }
    })();

    this.service.pendingRequests.set(requestKey, request);
    return request;
  }

  async getOfferings(): Promise<any> {
    if (!this.isReady()) {
      throw new Error('RevenueCat not initialized');
    }

    // Wait for billing client on Android
    await this.waitForBillingClient();

    const requestKey = 'getOfferings';
    const existingRequest = this.service.pendingRequests.get(requestKey);

    if (existingRequest) {
      console.log('🛒 [RevenueCat Service] Deduplicating getOfferings request');
      return existingRequest;
    }

    const request = (async () => {
      const timeout = this.getTimeoutForOperation('getOfferings');
      const tracer = createTracer('RevenueCat getOfferings', timeout);

      try {
        console.log('🛒 [RevenueCat Service] Fetching offerings from API...');
        console.log('🛒 [RevenueCat Service] Network quality:', this.networkQuality, '| Timeout:', timeout, 'ms');

        tracer.addCheckpoint('Starting getOfferings request');

        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('GetOfferings request timeout')), timeout)
        );

        const offerings = await Promise.race([
          this.service.purchases.getOfferings(),
          timeoutPromise
        ]);

        tracer.addCheckpoint('getOfferings completed');
        tracer.complete({ success: true });
        return offerings;
      } catch (error: any) {
        const duration = tracer.getDuration();
        const errorMsg = error?.message || '';
        const isTimeout = errorMsg.includes('timeout') || duration >= timeout;

        if (isTimeout) {
          tracer.timeout(duration);
          await timeoutLogger.logTimeout({
            requestId: tracer.getRequestId(),
            operationName: 'RevenueCat getOfferings',
            timeoutDuration: timeout,
            actualDuration: duration,
            errorCode: TimeoutErrorCode.REVENUECAT_OFFERINGS,
            severity: TimeoutSeverity.HIGH,
            metadata: {
              platform: Platform.OS,
              networkQuality: this.networkQuality,
            },
          });
        }

        throw error;
      } finally {
        this.service.pendingRequests.delete(requestKey);
      }
    })();

    this.service.pendingRequests.set(requestKey, request);
    return request;
  }

  async purchasePackage(packageToPurchase: any): Promise<any> {
    if (!this.isReady()) {
      throw new Error('RevenueCat not initialized');
    }

    // Wait for billing client on Android
    await this.waitForBillingClient();

    const requestKey = `purchasePackage:${packageToPurchase.identifier}`;
    const existingRequest = this.service.pendingRequests.get(requestKey);

    if (existingRequest) {
      console.log('🛒 [RevenueCat Service] Purchase already in progress for:', packageToPurchase.identifier);
      return existingRequest;
    }

    const request = (async () => {
      try {
        console.log('🛒 [RevenueCat Service] Purchasing package:', packageToPurchase.identifier);
        const result = await this.service.purchases.purchasePackage(packageToPurchase);

        this.service.lastCustomerInfo = result.customerInfo;
        this.service.lastCustomerInfoFetch = Date.now();

        return result;
      } finally {
        this.service.pendingRequests.delete(requestKey);
      }
    })();

    this.service.pendingRequests.set(requestKey, request);
    return request;
  }

  async restorePurchases(): Promise<any> {
    if (!this.isReady()) {
      throw new Error('RevenueCat not initialized');
    }

    // Wait for billing client on Android
    await this.waitForBillingClient();

    const requestKey = 'restorePurchases';
    const existingRequest = this.service.pendingRequests.get(requestKey);

    if (existingRequest) {
      console.log('🛒 [RevenueCat Service] Restore already in progress, waiting...');
      return existingRequest;
    }

    const request = (async () => {
      try {
        console.log('🛒 [RevenueCat Service] Restoring purchases...');
        const result = await this.service.purchases.restorePurchases();

        console.log('🛒 [RevenueCat Service] Restore result:', {
          hasCustomerInfo: !!result?.customerInfo,
          hasEntitlements: !!result?.customerInfo?.entitlements,
          originalAppUserId: result?.customerInfo?.originalAppUserId
        });

        if (!result || !result.customerInfo) {
          console.error('🛒 [RevenueCat Service] ❌ restorePurchases() returned invalid result');
          throw new Error('Invalid restore result: customerInfo is missing');
        }

        this.service.lastCustomerInfo = result.customerInfo;
        this.service.lastCustomerInfoFetch = Date.now();

        return result;
      } finally {
        this.service.pendingRequests.delete(requestKey);
      }
    })();

    this.service.pendingRequests.set(requestKey, request);
    return request;
  }

  async getCurrentAppUserId(): Promise<string> {
    if (!this.isReady()) {
      throw new Error('RevenueCat not initialized');
    }

    try {
      const customerInfo = await this.service.purchases.getCustomerInfo();
      return customerInfo.originalAppUserId;
    } catch (error) {
      console.error('🛒 [RevenueCat Service] Error getting current app user ID:', error);
      return '';
    }
  }

  async logIn(appUserId: string): Promise<any> {
    if (!this.isReady()) {
      throw new Error('RevenueCat not initialized');
    }

    // Wait for billing client on Android
    await this.waitForBillingClient();

    const lastFetchAge = Date.now() - this.service.lastCustomerInfoFetch;
    const isSameUser = this.service.currentAppUserId === appUserId;
    const hasFreshData = lastFetchAge < 5000;

    if (isSameUser && this.service.userIdentificationComplete && hasFreshData) {
      console.log('🛒 [RevenueCat Service] User already identified with ID:', appUserId, 'and data is fresh (', lastFetchAge, 'ms old) - skipping');
      return { customerInfo: this.service.lastCustomerInfo, created: false };
    }

    if (isSameUser && this.service.userIdentificationComplete) {
      console.log('🛒 [RevenueCat Service] User already identified but data is stale (', lastFetchAge, 'ms old) - re-identifying to fetch fresh data');
    }

    const requestKey = `logIn:${appUserId}`;
    const existingRequest = this.service.pendingRequests.get(requestKey);

    if (existingRequest) {
      console.log('🛒 [RevenueCat Service] Deduplicating logIn request for user:', appUserId);
      return existingRequest;
    }

    const request = (async () => {
      const timeout = this.getTimeoutForOperation('logIn');
      const tracer = createTracer('RevenueCat logIn', timeout);

      try {
        await this.waitForMinDelay(requestKey);
        tracer.addCheckpoint('Rate limit delay completed');

        console.log('🛒 [RevenueCat Service] Logging in user (identifying):', appUserId);
        console.log('🛒 [RevenueCat Service] Network quality:', this.networkQuality, '| Timeout:', timeout, 'ms');

        tracer.addCheckpoint('Starting logIn request');

        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('LogIn request timeout')), timeout)
        );

        const result: any = await Promise.race([
          this.retryOnConcurrentError(
            () => this.service.purchases.logIn(appUserId),
            'logIn'
          ),
          timeoutPromise
        ]);

        tracer.addCheckpoint('logIn request completed');
        console.log('🛒 [RevenueCat Service] ✓ User identified successfully');
        console.log('🛒 [RevenueCat Service] Customer Info originalAppUserId:', result.customerInfo?.originalAppUserId);

        if (result.customerInfo?.originalAppUserId?.startsWith('$RCAnonymousID:')) {
          console.warn('🛒 [RevenueCat Service] ⚠️ WARNING: Received anonymous customer info after logIn');
          console.warn('🛒 [RevenueCat Service] This indicates RevenueCat returned cached anonymous data');
          console.warn('🛒 [RevenueCat Service] Starting retry loop to fetch valid user data...');

          const maxRetries = 5;
          const baseDelay = 300;
          let validCustomerInfo = null;

          for (let attempt = 1; attempt <= maxRetries; attempt++) {
            const delay = baseDelay * attempt;
            console.log(`🛒 [RevenueCat Service] Retry ${attempt}/${maxRetries} - waiting ${delay}ms...`);
            await new Promise(resolve => setTimeout(resolve, delay));

            try {
              const freshCustomerInfo = await this.retryOnConcurrentError(
                () => this.service.purchases.getCustomerInfo(),
                `getCustomerInfo retry ${attempt}`
              );

              console.log(`🛒 [RevenueCat Service] Retry ${attempt} - originalAppUserId:`, freshCustomerInfo?.originalAppUserId);

              if (!freshCustomerInfo?.originalAppUserId?.startsWith('$RCAnonymousID:')) {
                console.log(`🛒 [RevenueCat Service] ✓ Valid user data received on retry ${attempt}`);
                validCustomerInfo = freshCustomerInfo;
                break;
              }

              if (freshCustomerInfo?.originalAppUserId === appUserId) {
                console.log(`🛒 [RevenueCat Service] ✓ Matching user ID received on retry ${attempt}`);
                validCustomerInfo = freshCustomerInfo;
                break;
              }
            } catch (retryError) {
              console.warn(`🛒 [RevenueCat Service] Retry ${attempt} failed:`, retryError);
            }
          }

          if (validCustomerInfo) {
            result.customerInfo = validCustomerInfo;
          } else {
            console.warn('🛒 [RevenueCat Service] ⚠️ Could not get valid user data after', maxRetries, 'retries');
            console.warn('🛒 [RevenueCat Service] Proceeding with available data - subscription status may need refresh');
          }
        }

        this.service.lastCustomerInfo = result.customerInfo;
        this.service.lastCustomerInfoFetch = Date.now();
        this.service.currentAppUserId = appUserId;
        this.service.userIdentificationComplete = true;
        tracer.complete({ success: true });

        await new Promise(resolve => setTimeout(resolve, 100));

        return result;
      } catch (error: any) {
        const duration = tracer.getDuration();
        const errorMsg = error?.message || '';
        const isTimeout = errorMsg.includes('timeout') || duration >= timeout;

        if (isTimeout) {
          tracer.timeout(duration);
          await timeoutLogger.logTimeout({
            requestId: tracer.getRequestId(),
            operationName: 'RevenueCat logIn',
            timeoutDuration: timeout,
            actualDuration: duration,
            errorCode: TimeoutErrorCode.REVENUECAT_LOGIN,
            severity: TimeoutSeverity.HIGH,
            metadata: {
              platform: Platform.OS,
              networkQuality: this.networkQuality,
              appUserId,
            },
          });
        }

        throw error;
      } finally {
        this.service.pendingRequests.delete(requestKey);
      }
    })();

    this.service.pendingRequests.set(requestKey, request);
    return request;
  }

  isUserIdentified(): boolean {
    return this.service.userIdentificationComplete;
  }

  getCredentialStatus(): { hasErrors: boolean; errorCount: number; lastError: number | null } {
    return {
      hasErrors: this.service.credentialErrorCount > 0,
      errorCount: this.service.credentialErrorCount,
      lastError: this.service.lastCredentialError
    };
  }

  getNetworkQuality(): string {
    return this.networkQuality;
  }

  reset(): void {
    console.log('🛒 [RevenueCat Service] Resetting service state');
    this.service.customerInfoListeners.clear();
    this.service.lastCustomerInfo = null;
    this.service.lastCustomerInfoFetch = 0;
    this.service.pendingRequests.clear();
    this.service.credentialErrorCount = 0;
    this.service.lastCredentialError = null;
    this.service.currentAppUserId = null;
    this.service.userIdentificationComplete = false;
    this.service.isInitialized = false;
    this.service.isInitializing = false;
    this.service.isBillingClientReady = false;
    this.service.billingClientRetryCount = 0;
    this.service.initializationPromise = null;
    this.service.purchases = null;
    this.networkQuality = 'UNKNOWN';
    globalThis.__RCConfigured = false;
    console.log('🛒 [RevenueCat Service] Service reset complete - ready for re-initialization');
  }

  clearUserState(): void {
    console.log('🛒 [RevenueCat Service] Clearing user identification state');
    this.service.currentAppUserId = null;
    this.service.userIdentificationComplete = false;
    this.service.lastCustomerInfo = null;
    this.service.lastCustomerInfoFetch = 0;
    console.log('🛒 [RevenueCat Service] User state cleared - next login will fetch fresh data');
  }
}

export const revenueCatService = RevenueCatServiceManager.getInstance();
